import { useContext, useMemo } from 'react';
import { AuthContext } from '@repo-level/core-front';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'hooks/theme';
import { MobileAppContext } from 'contexts/mobileApp';
import { AddonsContext, KYCVersion } from 'contexts/addons';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import {
  groupMember,
  groupMemberAskedForDocs,
  groupMemberVerified,
  USER_GROUP,
} from 'helpers/user';

import { useLuckyWheel } from './luckyWheel';
import { getSpinsLeftAmount } from 'modules/wheel/components/helpers';
import { ScNavBadge } from 'components/general/Menu/MenuDesktop/styled';

interface MenuItem {
  name: string;
  url: string;
  icon?: JSX.Element;
  iconSettings?: {
    isStroke?: boolean;
  };
  badge?: JSX.Element | null;
  isHidden?: boolean;
}

export const useHeaderMenuList = () => {
  const { t } = useTranslation();
  const { images } = useTheme();
  const { isAuthenticated, pendingUser, user } = useContext(AuthContext);
  const { lootboxes } = useContext(RealTimeServiceContext);
  const { shouldShowIos, shouldShowAndroid } = useContext(MobileAppContext);
  const { kycVersion } = useContext(AddonsContext);

  const { isExtraLootbox } = useLuckyWheel();

  const isVerifiedUser = useMemo(() => groupMemberVerified(user), [user]);

  const isAskedForDocsUser = useMemo(() => groupMemberAskedForDocs(user), [
    user,
  ]);

  const spinsLeftAmount = useMemo(() => {
    return isAuthenticated ? getSpinsLeftAmount(lootboxes || [], user) : null;
  }, [isAuthenticated, lootboxes, user]);

  const headerMenuList = useMemo(
    (): MenuItem[] => [
      {
        name: t('glossary:home'),
        url: '/',
        icon: <images.homeIcon.component />,
      },
      {
        name: t('glossary:fortune'),
        url: '/bonuses',
        icon: <images.fortuneIcon.component />,
      },
      {
        name: t('glossary:luckyWheel'),
        url: '/lucky-wheel',
        icon: <images.wheelIcon.component />,
        badge:
          !!spinsLeftAmount || isExtraLootbox ? (
            <ScNavBadge withOpacity={!isExtraLootbox}>
              <div className="bg" />
              <div className="content">
                {isExtraLootbox ? <images.star.component /> : spinsLeftAmount}
              </div>
            </ScNavBadge>
          ) : null,
        isHidden: pendingUser || (user && groupMember(USER_GROUP.BR2)(user)),
      },
      // {
      //   name: t('glossary:rewardLineup'),
      //   url: '/advent-mission',
      //   icon: <images.rewardLineupIcon.component />,
      // },
      {
        name: t('glossary:jackPot'),
        url: '/jackpot',
        icon: <images.jackpotIcon.component />,
      },
      {
        name: t('glossary:tournaments'),
        url: '/tournaments',
        icon: <images.tournamentIcon.component />,
      },
      {
        name: t('glossary:loyalty'),
        url: '/loyalty',
        icon: <images.loyaltyIcon.component />,
      },
      {
        name: t('glossary:vip'),
        url: '/vip',
        icon: <images.vipIcon.component />,
        iconSettings: { isStroke: true },
      },
      {
        name: t('glossary:searchGames'),
        url: '/search-games',
        icon: <images.searchIcon.component />,
      },
      {
        name: t('glossary:profile'),
        url: '/profile-custom/personal',
        icon: <images.profileIcon.component />,
        badge:
          kycVersion === KYCVersion.B_TEST &&
          (isVerifiedUser || isAskedForDocsUser) ? (
            <ScNavBadge>
              {isVerifiedUser && <images.verified.component />}
              {isAskedForDocsUser && <images.unverified.component />}
            </ScNavBadge>
          ) : null,
        isHidden: !isAuthenticated,
      },
      {
        name: t('glossary:mobileApp'),
        url: '/mobile-app',
        icon: <images.mobileAppIcon.component />,
        isHidden: !(shouldShowIos || shouldShowAndroid),
      },
    ],
    [
      images,
      isAskedForDocsUser,
      isAuthenticated,
      isExtraLootbox,
      isVerifiedUser,
      kycVersion,
      pendingUser,
      shouldShowAndroid,
      shouldShowIos,
      spinsLeftAmount,
      t,
      user,
    ]
  );
  return headerMenuList;
};
